/**
 * Features and Pages API Service
 *
 * The catalogue the platform's own menu is generated from: sections contain
 * features, features contain pages. Reads are open to any admin; every write
 * is platform-administrator only and the API returns 403 otherwise.
 */

import { get, post, patch } from './client';

/**
 * The whole catalogue, nested and in display order.
 * @returns {Promise<{items: Array, editable: boolean}>} `editable` reflects
 *   whether this caller may write — the controls key off it.
 */
export const getCatalogue = () => get('/catalogue');

export const createSection = (data) => post('/catalogue/sections', data);

export const createFeature = (data) => post('/catalogue/features', data);
export const updateFeature = (featureId, data) => patch(`/catalogue/features/${featureId}`, data);

export const createPage = (data) => post('/catalogue/pages', data);
export const updatePage = (pageId, data) => patch(`/catalogue/pages/${pageId}`, data);

/**
 * Swap a feature with its neighbour in the section.
 * @param {string} direction - 'up' | 'down'
 */
export const reorderFeature = (featureId, direction) => post(`/catalogue/features/${featureId}/reorder`, { direction });

/** Swap a page with its neighbour in the feature. */
export const reorderPage = (pageId, direction) => post(`/catalogue/pages/${pageId}/reorder`, { direction });

/**
 * The same catalogue crossed with every role, read from the stored permissions
 * rather than from a constant in the screen. Reference only — nothing here is
 * editable, so there is no matching write.
 * @returns {Promise<{roles: Array, levels: Array, sections: Array}>}
 */
export const getAccessMatrix = () => get('/catalogue/access-matrix');

/**
 * Which features and pages are switched OFF, for gating navigation.
 *
 * Distinct from `getMyFeatures()`: that is product-tier entitlement (what the
 * organization bought), this is the platform switch (whether the thing is on at
 * all). A screen needs both to appear.
 *
 * @returns {Promise<{disabled_features: Array, disabled_pages: Array, protected_codes: string[]}>}
 */
export const getCatalogueVisibility = () => get('/catalogue/visibility');

/**
 * The navigation the signed-in user has actually been granted, built from the same
 * role-permission rows Role Definitions writes. This is what makes a newly created
 * role usable: its ticked features become that user's sidebar and its reachable routes.
 *
 * @returns {Promise<{sections: Array, landing: string|null, is_super_admin: boolean}>}
 */
export const getMyMenu = () => get('/catalogue/my-menu');

export default {
  getCatalogue,
  getMyMenu,
  getCatalogueVisibility,
  createSection,
  createFeature,
  updateFeature,
  createPage,
  updatePage,
  reorderFeature,
  reorderPage,
  getAccessMatrix
};
